import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import type { PlaneType } from '../../../../game/Constants';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { getViewportSize } from '../../../Responsive';
import { selectorBadges, selectorIcons } from '../../../theme/selector.css';
import { tokens } from '../../../theme/tokens.css';
import { toolbarChip } from '../../../theme/toolbar.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { AIRCRAFT, MAX_UPGRADE_LEVEL, UPGRADES } from '../loadout-data';

/** Vertical gap between the loadout button and the popover top. */
const POPOVER_TOP_GAP = 10;
/** Must match `.panel { width }` below (border-box, so padding included) —
 *  used to clamp the popover inside the viewport when the button sits near a
 *  screen edge. */
const POPOVER_WIDTH = 260;
/** Minimum gap between the popover and the viewport edges. */
const POPOVER_VIEWPORT_MARGIN = 8;
/** Window event that opens the popover from outside the chip — dispatched by
 *  the spectator overlay's mobile RESPAWN button. Open-only (not a toggle). */
export const OPEN_LOADOUT_PANEL_EVENT = 'open-loadout-panel';

/** Popover height with slack (measured 168px: two section titles, two chip
 *  rows, padding), for the clamp above the viewport bottom. The chip sits in
 *  the top bar, so the clamp only bites on a viewport under about 256px tall
 *  — a guard, not a layout rule. */
const POPOVER_ESTIMATED_HEIGHT = 180;

/**
 * Loadout Panel - Mobile replacement for the desktop sidebar selectors
 *
 * A toolbar chip (rendered in the top bar, mobile only) that opens a
 * compact popover with the plane picker and upgrade buttons — the two sidebar
 * surfaces that are hidden on mobile to clear the touch-control zones. Cruise
 * mode deliberately stays out: it's a mid-flight combat control, not a loadout
 * decision, and keeps its own on-screen button.
 *
 * The chip shows a count badge while upgrade points are banked — the mobile
 * stand-in for the sidebar's green glow.
 *
 * Interactions: tapping a plane selects it and closes the popover (it respawns
 * you, so the panel gets out of the way); tapping an upgrade spends one point
 * and stays open for repeat taps. Tapping outside closes. A spectator's
 * RESPAWN button (spectator-overlay) opens it via OPEN_LOADOUT_PANEL_EVENT,
 * since this panel is the mobile way back into the game. Shares the top-bar
 * "only one popover open" coordination (`openTopBarPanel`) with zoom-slider
 * and game-selector.
 *
 * @element loadout-panel
 */
@customElement('loadout-panel')
export class LoadoutPanel extends LitElement {
  static override styles = [
    tokens,
    toolbarChip,
    selectorIcons,
    selectorBadges,
    css`
      :host {
        display: block;
        pointer-events: auto;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow tap on the popover shouldn't
           start an iOS text selection or callout on its titles. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      /* Banked-upgrade count. Yellow = "attention", same accent the
         leaderboard uses for the active-players count. */
      .badge {
        position: absolute;
        top: 1px;
        right: 1px;
        min-width: 13px;
        height: 13px;
        box-sizing: border-box;
        padding: 0 3px;
        border-radius: 7px;
        background-color: var(--color-players-active);
        color: #000;
        font-size: var(--font-size-xxs);
        font-weight: 700;
        line-height: 13px;
        text-align: center;
        pointer-events: none;
      }

      .panel {
        position: fixed;
        z-index: 200;
        width: 260px;
        box-sizing: border-box;
        background-color: var(--color-bg-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 10px 12px 12px;
        pointer-events: auto;
        cursor: default;
        /* Upgrades stay open for repeat taps, and two quick taps must spend
           two points — without this iOS can read them as a double-tap zoom
           and swallow the second. Covers the gaps between chips too. */
        touch-action: manipulation;
      }

      .section-title {
        font-size: var(--font-size-xs);
        font-weight: bold;
        color: var(--color-text-tertiary);
        margin: 2px 2px 6px;
      }

      .section-title .available {
        color: var(--color-players-active);
        margin-left: 6px;
      }

      .row {
        display: flex;
        gap: 4px;
      }

      .row + .section-title {
        margin-top: 12px;
      }

      .chip {
        position: relative;
        width: 44px;
        height: 44px;
        background-color: var(--color-bg-selector);
        background-repeat: no-repeat;
        background-size: 44px 44px;
        background-position: 0 0;
        border-radius: var(--radius-md);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }

      /* Current plane — highlighted as "you", and non-interactive (you can't
         switch to the plane you're already flying). Inset ring reads as a
         selection state without shifting layout. */
      .chip.selected {
        background-color: var(--color-bg-selector-hover);
        cursor: default;
        box-shadow: inset 0 0 0 2px var(--color-text-white);
      }

      .chip.lighted {
        background-color: var(--color-bg-selector-lighted);
        box-shadow: var(--shadow-lighted);
      }

      /* The plane and upgrade icons (keyed by cssClass / upgrade.key), the
         .level counter and the CTF .count-badge come from selectorIcons /
         selectorBadges — the same ones the desktop sidebar uses. */
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** Whether the popover is open */
  @state() private isOpen = false;

  /** Viewport-relative position for the fixed popover, computed from the
   *  button's bounding rect on open and on resize. */
  @state() private popoverLeft = 0;
  @state() private popoverTop = 0;

  /** @override Registers outside-tap + resize listeners */
  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener(OPEN_LOADOUT_PANEL_EVENT, this.handleOpenRequest);
    // iOS collapses its toolbar without firing a window resize, so an open
    // popover would keep a clamp measured against chrome that is gone.
    window.visualViewport?.addEventListener('resize', this.handleResize);
  }

  /** @override Cleans up listeners and releases the top-bar slot if we still
   *  own it (e.g. the bar unmounts while we're open). */
  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener(OPEN_LOADOUT_PANEL_EVENT, this.handleOpenRequest);
    window.visualViewport?.removeEventListener('resize', this.handleResize);
    if (this.isOpen) this.close();
  }

  /** Close ourselves when a sibling top-bar panel becomes the active one. */
  protected override willUpdate(): void {
    if (this.isOpen && this.uiState.openTopBarPanel !== 'loadout-panel') {
      this.isOpen = false;
    }
  }

  /** Re-snap the fixed popover to the button after orientation/resize. */
  private handleResize = () => {
    if (this.isOpen) this.recomputePopoverPosition();
  };

  private recomputePopoverPosition(): void {
    const rect = this.getBoundingClientRect();
    // The visible area, not window.inner* — on iOS the layout viewport stays
    // tall while Safari's toolbar covers the bottom. Same as zoom-slider.
    const viewport = getViewportSize();
    // Clamp both axes so the popover stays inside the viewport. Horizontally
    // it matters on narrow portrait phones: the chip's left edge is ~119px in,
    // so a 260px popover runs past the 8px margin on anything under ~387px
    // wide (a 375px iPhone clamps it 12px left).
    this.popoverLeft = Math.max(
      POPOVER_VIEWPORT_MARGIN,
      Math.min(rect.left, viewport.width - POPOVER_WIDTH - POPOVER_VIEWPORT_MARGIN),
    );
    this.popoverTop = Math.max(
      POPOVER_VIEWPORT_MARGIN,
      Math.min(rect.bottom + POPOVER_TOP_GAP, viewport.height - POPOVER_ESTIMATED_HEIGHT - POPOVER_VIEWPORT_MARGIN),
    );
  }

  /** Close when tapping anywhere outside the component (game world, other
   *  toolbar buttons, ...). composedPath for shadow DOM compatibility. */
  private handleClickOutside = (e: MouseEvent) => {
    if (this.isOpen && !e.composedPath().includes(this)) {
      this.close();
    }
  };

  /** Toggle on button tap. stopPropagation keeps our own document-level
   *  outside-tap handler from immediately re-closing. */
  private handleToggle = (e: Event) => {
    e.stopPropagation();
    this.gameApi.playClick();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /** Open request from elsewhere — the spectator overlay's RESPAWN button.
   *  Open-only, never a toggle, and silent: the sender is the tap and plays
   *  its own click. The sender must stopPropagation its click, or it reaches
   *  handleClickOutside on the document and closes us straight away. */
  private handleOpenRequest = () => {
    if (!this.isOpen) this.open();
  };

  private open(): void {
    // Compute coords BEFORE flipping isOpen so the fixed popover paints at
    // the right spot on its first frame.
    this.recomputePopoverPosition();
    this.isOpen = true;
    this.gameApi.setOpenTopBarPanel('loadout-panel');
  }

  /** Shared close path. The setOpenTopBarPanel null is guarded so the
   *  willUpdate path (where a sibling already grabbed the slot) doesn't wipe
   *  their claim. */
  private close(): void {
    this.isOpen = false;
    if (this.uiState.openTopBarPanel === 'loadout-panel') {
      this.gameApi.setOpenTopBarPanel(null);
    }
  }

  /** The plane you're in, or null while spectating. selectedAircraft still
   *  holds the last plane flown then, but you aren't in it — and this panel is
   *  a mobile spectator's only way back (the spectator overlay has no respawn
   *  button), so all five chips must respawn. Dead-but-not-spectating keeps
   *  the plane: the server respawns you in it automatically. */
  private get currentPlane(): PlaneType | null {
    return this.uiState.spectator?.mode === 'spectating' ? null : this.uiState.selectedAircraft;
  }

  /** Plane tap: selecting respawns you, so the panel gets out of the way.
   *  Tapping the plane you're already flying is a no-op — its chip is shown
   *  as the highlighted, non-interactive current selection. */
  private handleSelectPlane(type: PlaneType): void {
    if (type === this.currentPlane) return;
    this.gameApi.playClick();
    this.gameApi.selectAircraft(type);
    this.close();
  }

  /** Upgrade tap: spends one point; stays open for repeat taps. */
  private handleSelectUpgrade(index: 1 | 2 | 3 | 4): void {
    this.gameApi.playClick();
    this.gameApi.selectUpgrade(index);
  }

  /** True when there are banked points AND at least one upgrade isn't maxed —
   *  i.e. points the player can actually spend. A fully-maxed loadout with
   *  leftover points shouldn't nag (the badge / "available" count hide). */
  private get canSpendUpgrades(): boolean {
    const { availableUpgrades, upgradeLevels } = this.uiState;
    return availableUpgrades > 0 && UPGRADES.some((u) => (upgradeLevels[u.key] ?? 0) < MAX_UPGRADE_LEVEL);
  }

  override render() {
    return html`
      <button class="toolbar-chip" @click=${this.handleToggle}>
        <img src="/vanilla/ui/menu/plane.svg" alt="Loadout" />
        ${this.canSpendUpgrades ? html`<span class="badge">${this.uiState.availableUpgrades}</span>` : nothing}
      </button>
      ${this.isOpen ? this.renderPanel() : nothing}
    `;
  }

  /** Renders the open popover: plane row + upgrade row. */
  private renderPanel() {
    const { upgradeLevels, availableUpgrades, ctfPlaneRosters } = this.uiState;
    const currentPlane = this.currentPlane;

    return html`
      <div class="panel" style="left: ${this.popoverLeft}px; top: ${this.popoverTop}px">
        <div class="section-title">PLANE</div>
        <div class="row">
          ${AIRCRAFT.map((aircraft) => {
            const isCurrent = currentPlane === aircraft.type;
            const roster = ctfPlaneRosters?.[aircraft.type] ?? null;
            const blueCount = roster?.blue.length ?? 0;
            const redCount = roster?.red.length ?? 0;

            return html`
              <div
                class="chip ${aircraft.cssClass} ${isCurrent ? 'selected' : ''}"
                role="button"
                aria-label=${aircraft.name}
                aria-current=${isCurrent ? 'true' : 'false'}
                @click=${() => this.handleSelectPlane(aircraft.type)}
              >
                ${roster && blueCount > 0 ? html`<span class="count-badge blue">${blueCount}</span>` : nothing}
                ${roster && redCount > 0 ? html`<span class="count-badge red">${redCount}</span>` : nothing}
              </div>
            `;
          })}
        </div>

        <div class="section-title">
          UPGRADES
          ${this.canSpendUpgrades ? html`<span class="available">${availableUpgrades} available</span>` : nothing}
        </div>
        <div class="row">
          ${UPGRADES.map((upgrade) => {
            // Clamp defensively — level is server-sourced and should be 0..MAX,
            // but a stray value would otherwise render a nonsensical badge.
            const level = Math.max(0, Math.min(MAX_UPGRADE_LEVEL, upgradeLevels[upgrade.key] ?? 0));
            const isLighted = availableUpgrades > 0 && level < MAX_UPGRADE_LEVEL;

            return html`
              <div
                class="chip ${upgrade.key} ${isLighted ? 'lighted' : ''}"
                role="button"
                aria-label=${upgrade.name}
                @click=${() => this.handleSelectUpgrade(upgrade.index)}
              >
                <div class="level">${level}</div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'loadout-panel': LoadoutPanel;
  }
}
